import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import {
    ToastContext,
    CoreFetch,
    CollectionApi,
    ProviderUpdateCollectionPaymentPayload,
    PaymentApi,
    ProviderClaim,
} from '@enter/core';
import { IObservedRootStore } from 'stores';
import { DialogContext } from 'services';
import { PageLayout } from 'components';
import {
    StatementDetailsPage,
    IStatementDetailsProps,
    createStatementAdapter,
    buildClaimRowHref,
} from '@enter/core/src/ui/patient-statement';
import { equalsFilter } from 'stores/util';

export const StatementDetails: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore): React.ReactElement => {
        const { rootStore } = props;
        const { patientId, accountId } = useParams<{ patientId: string; accountId?: string }>();
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);

        const adapter = useMemo(() => {
            if (!rootStore) return null;
            return createStatementAdapter({
                equalsFilter,
                loadPatientStatement: id => rootStore.patientStore.loadPatientStatement(id),
                claimStore: {
                    createCollectionPayment: payload => rootStore.claimStore.createCollectionPayment(payload),
                    updateCollectionPayment: async (id: string, payload: ProviderUpdateCollectionPaymentPayload) => {
                        return CoreFetch<CollectionApi>(CollectionApi).patchProviderUpdateCollectionPayment({
                            id,
                            providerUpdateCollectionPaymentPayload: {
                                amount: payload.amount,
                                claimId: payload.claimId,
                            },
                        });
                    },
                    deleteCollectionPayment: async () => {
                        throw new Error('Delete collection payment is not supported for Provider');
                    },
                    refundCollectionPayment: async (paymentId, payload) => {
                        return rootStore.claimStore.refundCollectionPayment(paymentId, payload);
                    },
                },
                dialogContext: {
                    showDialog: dialogProps => dialogContext.showDialog(dialogProps),
                    hideDialog: () => dialogContext.hideDialog(),
                    showError: err => dialogContext.showError(err),
                },
                listClaimPayments: async claimIds =>
                    CoreFetch<PaymentApi>(PaymentApi).listProviderClaimPayments({
                        filterClaimId: equalsFilter(claimIds),
                    }),
            });
        }, [rootStore, dialogContext]);

        const statementDetailsProps: IStatementDetailsProps | null = useMemo(() => {
            if (!patientId || !adapter) return null;
            return {
                patientId,
                ...adapter,
                toastContext,
                PageLayout,
                getClaimRowHref: (claim: ProviderClaim) => buildClaimRowHref(accountId, claim.id),
                aiUtilitySide: 'Provider',
                showActions: true,
            };
        }, [patientId, accountId, adapter, toastContext]);

        if (!statementDetailsProps) {
            return <></>;
        }

        return <StatementDetailsPage {...statementDetailsProps} />;
    }),
);
